/* eslint-disable max-len */
import React, { useState, useEffect, useMemo } from 'react';
import I18N from '@leverege/i18n';
import { Button, Checkbox, CompositeTextInput, Content, Flex, Pane, Text } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import { Validator } from '@leverege/validator';
import { DataSources } from '@leverege/ui-attributes';
import S from '../../views/salesFlow/screens/PendingSaleMessageUserList.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('recovr.alerts.views.SearchRecipients');
function SearchRecipients({
  actions: pActions,
  state: pState,
  onCancel,
  selectionLimit,
  // pass as prop to limit the number of recipients selected, defaults to no limit
  searchPhones,
  // pass as prop to enable phone search
  searchEmails,
  // pass as prop to enable email search
  searchUsers,
  // pass as prop to enable user search
  vehicles
}) {
  const [state, setState] = pState;
  const [locationUsers, setLocationUsers] = useState([]);
  const [users, setUsers] = useState(state?.pendingSaleMessageRecipients?.filter(user => typeof user === 'object') || []);
  const [phones, setPhones] = useState(state?.pendingSaleMessageRecipients?.filter(phone => typeof phone === 'string' && !phone.includes('@')) || []);
  const [emails, setEmails] = useState(state?.pendingSaleMessageRecipients?.filter(email => Validator.isEmail(email) && typeof email === 'string' && email.includes('@')) || []);
  const [selected, setSelected] = useState(state?.pendingSaleMessageRecipients || []);
  const [search, setSearch] = useState('');

  // initialize data in search screen
  useEffect(() => {
    let lActions = pActions;
    if (vehicles) {
      const actions = pActions || DataSources.getActions(vehicles[0]);
      if (!actions.relationship.options.isItem && actions.relationship.options.objectType === 'sale') {
        lActions = actions.parent();
      } else {
        lActions = actions.parent().parent();
      }
    }
    const fetchData = async () => {
      try {
        if (searchUsers) {
          const fetchedUsers = await GlobalState.dispatch(lActions.users().list({
            limit: 999
          }));
          setLocationUsers(fetchedUsers.items.filter(user => !!user.email));
          setUsers(state?.pendingSaleMessageRecipients.filter(user => typeof user === 'object') || []);
        }
        if (searchPhones) {
          setPhones(state?.pendingSaleMessageRecipients?.filter(phone => typeof phone === 'string' && !phone.includes('@')) || []);
        }
        if (searchEmails) {
          setEmails(state?.pendingSaleMessageRecipients?.filter(email => typeof email === 'string' && email.includes('@')) || []);
        }
      } catch (err) {
        console.error('Could not fetch users searching for recipients', err);
      }
    };
    fetchData();
  }, [pActions, state?.pendingSaleMessageRecipients, vehicles]);

  // Add/remove unselected phone/email from phones/emails depending on search value.
  useEffect(() => {
    if (searchPhones) {
      if (search.length > 0 && Validator.isPhone(search) && !users.some(user => user?.phone?.replace(/\D/g, '')?.includes(search.replace(/\D/g, '')))) {
        setPhones([...(state?.pendingSaleMessageRecipientsTmp?.filter(r => typeof r === 'string' && !r.includes('@') && r.replace(/\D/g, '') !== search.replace(/\D/g, '').toLowerCase().trim()) || []), search]);
      } else {
        setPhones([...(state?.pendingSaleMessageRecipientsTmp?.filter(r => typeof r === 'string' && !r.includes('@')) || [])]);
      }
    }
    if (searchEmails) {
      if (search.length > 0 && Validator.isEmail(search) && !users.some(user => user?.email?.toLowerCase()?.trim()?.includes(search.toLowerCase().trim()))) {
        setEmails([...(state?.pendingSaleMessageRecipientsTmp?.filter(r => typeof r === 'string' && r.includes('@') && r.toLowerCase()?.trim() !== search.toLowerCase().trim()) || []), search]);
      } else {
        setEmails([...(state?.pendingSaleMessageRecipientsTmp?.filter(r => typeof r === 'string' && r.includes('@')) || [])]);
      }
    }
  }, [search, state?.pendingSaleMessageRecipientsTmp, users, searchEmails, searchPhones]);

  // Update parent state with the temporarily selected user list.
  useEffect(() => {
    setState({
      ...state,
      pendingSaleMessageRecipientsTmp: selected
    });
  }, [selected]);

  // wrapper to filter data based on search value
  const filterData = (data, search, filters) => {
    if (search.length === 0) {
      return [];
    }
    if (search.length >= 3) {
      return data.filter(filters);
    }
    return [];
  };

  // Filtered user, phones, and emails list
  const [filteredUsers, filteredPhones, filteredEmails] = useMemo(() => {
    const userFilter = user => user?.name?.toLowerCase()?.trim()?.includes(search.toLowerCase().trim()) || user?.email?.toLowerCase()?.trim()?.includes(search.toLowerCase().trim()) || user?.phone?.replace(/\D/g, '')?.includes(search.replace(/\D/g, '') || null);
    const phoneFilter = phone => phone.replace(/\D/g, '').includes(search.replace(/\D/g, '') || null);
    const emailFilter = email => email.toLowerCase()?.trim()?.includes(search.toLowerCase().trim());
    return [searchUsers ? filterData(locationUsers, search, userFilter) : [], searchPhones ? filterData(phones, search, phoneFilter) : [], searchEmails ? filterData(emails, search, emailFilter) : []];
  }, [locationUsers, phones, emails, search, searchPhones, searchEmails, searchUsers]);

  // Count of valid results
  const validResultsCount = useMemo(() => {
    const numFilteredPhones = filteredPhones.filter(phone => Validator.isPhone(phone)).length;
    const numFilteredEmails = filteredEmails.filter(email => Validator.isEmail(email)).length;
    const numFilteredUsers = filteredUsers.filter(user => typeof user === 'object').length;
    return numFilteredPhones + numFilteredEmails + numFilteredUsers;
  }, [filteredPhones, filteredEmails, filteredUsers]);
  const selectedIds = selected.map(({
    id
  }) => id);
  const searchMethods = searchPhones && searchEmails ? t('noResultsMsgPhonesAndEmails') : searchPhones ? t('noResultsMsgPhones') : t('noResultsMsgEmails');
  const setSelectionLimit = selectionLimit ? selected.length >= selectionLimit : false;
  return /*#__PURE__*/_jsxs(Content, {
    className: S.content,
    children: [/*#__PURE__*/_jsx(Content.Header, {
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "colL",
        className: S.contentHeaderFlex,
        children: [/*#__PURE__*/_jsx(CompositeTextInput, {
          autoFocus: true,
          onFocus: () => {
            setTimeout(() => window?.scrollTo(0, 0), 250);
          },
          pre: /*#__PURE__*/_jsx(Button, {
            variant: "searchBack",
            onClick: onCancel
          }),
          post: search && search.length > 0 && /*#__PURE__*/_jsx(Button, {
            variant: "pairClose",
            onClick: () => setSearch('')
          }),
          variant: "fullWidth",
          value: search,
          onChange: ({
            value
          }) => setSearch(value),
          changeOnReturn: false,
          alwaysFireOnReturn: false
        }), /*#__PURE__*/_jsxs(Flex, {
          variant: "colS",
          children: [/*#__PURE__*/_jsxs(Text, {
            variant: "bodySmall",
            className: S.usersCount,
            children: [validResultsCount, " ", t('results')]
          }), search.length < 3 && /*#__PURE__*/_jsx(Text, {
            variant: "bodySmall",
            children: t('searchRequirement')
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(Content.Area, {
      variant: "itemFormBody|formBody",
      className: S.form,
      children: /*#__PURE__*/_jsxs(Flex, {
        variant: "colL",
        children: [validResultsCount === 0 && /*#__PURE__*/_jsxs(Text, {
          variant: "bodySmall",
          children: [/*#__PURE__*/_jsx("b", {
            children: t('noResultsFound')
          }), t('noResultsMsgStart'), searchMethods, t('noResultsMsgEnd')]
        }), filteredUsers.map(user => /*#__PURE__*/_jsx(User, {
          user: user,
          value: selectedIds.includes(user.id),
          disabled: !selectedIds.includes(user.id) && setSelectionLimit,
          onChange: ({
            value
          }) => setSelected(s => value ? [...s, user] : s.filter(i => i?.id !== user.id))
        }, user.id)), filteredPhones.map(phone => /*#__PURE__*/_jsx(Phone, {
          phone: phone,
          value: selected.includes(phone),
          disabled: !selected.includes(phone) && setSelectionLimit,
          onChange: ({
            value
          }) => {
            if (phone !== search) {
              setPhones(phones.filter(i => i !== phone));
            }
            setSelected(s => value ? [...s, phone] : s.filter(i => i !== phone));
          }
        }, phone)), filteredEmails.map(email => /*#__PURE__*/_jsx(Email, {
          email: email,
          value: selected.includes(email),
          disabled: !selected.includes(email) && setSelectionLimit,
          onChange: ({
            value
          }) => {
            if (email !== search) {
              setEmails(emails.filter(i => i !== email));
            }
            setSelected(s => value ? [...s, email] : s.filter(i => i !== email));
          }
        }, email))]
      })
    })]
  });
}
function User({
  user,
  value,
  disabled,
  onChange
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    variant: "rowL",
    children: [/*#__PURE__*/_jsx(Checkbox, {
      value: value,
      onChange: onChange,
      disabled: disabled || !(Validator.isPhone(user?.phone) || Validator.isEmail(user?.email))
    }), /*#__PURE__*/_jsxs(Flex, {
      variant: "col",
      children: [/*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.name,
        children: user?.name || '-'
      }), /*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.roles,
        children: user?.roles?.filter(role => role?.metadata?.priority != null).sort((a, b) => a.metadata.priority - b.metadata.priority).map(role => role.displayName).join(', ') || '-'
      }), /*#__PURE__*/_jsx(Text, {
        variant: "bodySmall",
        children: user?.email || '-'
      }), user?.phone && /*#__PURE__*/_jsx(Text, {
        variant: "bodySmall",
        children: user?.phone
      })]
    })]
  });
}
function Phone({
  phone,
  value,
  disabled,
  onChange
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    variant: "rowL",
    children: [/*#__PURE__*/_jsx(Checkbox, {
      value: value,
      onChange: onChange,
      disabled: disabled || !Validator.isPhone(phone)
    }), /*#__PURE__*/_jsxs(Flex, {
      variant: "col",
      children: [/*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.name,
        children: `"${phone}"`
      }), Validator.isPhone(phone) ? /*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.validPhone,
        children: t('validPhone')
      }) : /*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.invalidPhone,
        children: t('invalidPhone')
      })]
    })]
  });
}
function Email({
  email,
  value,
  disabled,
  onChange
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    variant: "rowL",
    children: [/*#__PURE__*/_jsx(Checkbox, {
      value: value,
      onChange: onChange,
      disabled: disabled || !Validator.isEmail(email)
    }), /*#__PURE__*/_jsxs(Flex, {
      variant: "col",
      children: [/*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.name,
        children: `"${email}"`
      }), Validator.isEmail(email) ? /*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.validPhone,
        children: t('validEmail')
      }) : /*#__PURE__*/_jsx(Text, {
        variant: "bodySmallBold",
        className: S.invalidPhone,
        children: t('invalidEmail')
      })]
    })]
  });
}
export default SearchRecipients;